import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import strings from '../../../constants/strings.constants';
import {
  formatCurrency,
  getPartnerAddress,
  getPartnerName,
  getProductOptionTotalPrice,
} from '../../../utils/utils';
import { ROUTER_PATHS } from '../../../routes/routes.constatns';
import { getReservationSuccessLottieData } from '../../../utils/lottie';
import colors from '../../../constants/colors.constants';
import Button from '../../../components/atom/Button';
import Lottie from 'react-lottie';
import Typo from '../../../components/atom/Typo';
import moment from 'moment';
import useAuthStore from '../../../store/useAuthStore';
import { getUA, isAndroid, isIOS } from 'react-device-detect';
import useLanguageCodeStore from '../../../store/useLanguageCodeStore';
import { sendTutorialEndEvent } from '../../../store/useTutorialStore';
import { renderHardCodedReservationNotice } from '../../../hard-coded/hardCoding';

function ReservationCompletePage() {
  const { state = {}, search } = useLocation();
  const navigate = useNavigate();
  const { languageCode } = useLanguageCodeStore();
  const { partner } = useAuthStore();

  const customLabel =
    partner?.productViewConfig?.mimoWebViewConfig?.customLabel;

  const [reservationInfo, setReservationInfo] = useState<{
    name: string;
    reservedAt: string;
    partnerName: string;
    partnerAddress: string;
    expectPriceText: string;
    productText: string;
  }>({
    name: '',
    reservedAt: '',
    partnerName: '',
    partnerAddress: '',
    expectPriceText: '',
    productText: '',
  });

  const isDrPetit = useMemo(() => {
    if (partner) {
      return partner.partnerCode === 'drpetit';
    }
    return false;
  }, [partner]);

  useEffect(() => {
    if (!state) {
      navigate(ROUTER_PATHS.EVENT_TREATMENT_RESERVATION, { replace: true });
    }

    if (state) {
      let name = '';
      let reservedAtText = '';
      let partnerNameText = '';
      let partnerAddress = '';
      let expectPriceText = strings.noExpectPrice;
      let productText = strings.consultingReservation;

      const {
        partnerCustomer,
        reservedAt,
        expectPrice,
        reservationProductOptions = [],
        reservationType = '',
        parentCategoryName = '',
        childCategoryName = '',
      } = state;
      if (partnerCustomer) {
        const { customer } = partnerCustomer;
        if (customer) {
          const { customerName } = customer;
          name = customerName;
        }
      }
      if (partner) {
        partnerAddress = getPartnerAddress(partner);
        partnerNameText = getPartnerName(partner);
      }
      if (reservedAt && moment(reservedAt)) {
        reservedAtText = moment(reservedAt)
          .tz('Asia/Seoul')
          .format('YY.MM.DD (dd) HH:mm');
        if (languageCode !== 'ko') {
          reservedAtText = moment(reservedAt)
            .tz('Asia/Seoul')
            .format('YY.MM.DD (dd) HH:mm [(KST)]');
        }
      }

      if (expectPrice) {
        expectPriceText = `${formatCurrency(expectPrice)}${strings.priceUnit}`;
      }

      if (reservationProductOptions.length > 0) {
        const [option] = reservationProductOptions;
        const { productOptionName } = option;

        productText = `${productOptionName}`;
        if (reservationProductOptions.length > 1) {
          productText = strings.reservedProducts
            .replace('{productOptionName}', productOptionName)
            .replace('{n}', `${reservationProductOptions.length - 1}`);
        }

        expectPriceText = `${formatCurrency(getProductOptionTotalPrice(reservationProductOptions))}${strings.priceUnit}`;
      } else {
        let categoryText = '';
        if (parentCategoryName) {
          categoryText += parentCategoryName;
        }
        if (childCategoryName) {
          categoryText += `/${childCategoryName}`;
        }
        if (reservationType === 'REVISIT') {
          productText = `[${strings.treatmentReservationTag}] ${categoryText}`;
        } else if (reservationType === 'TREATMENT') {
          productText = `[${strings.treatmentReservationTag}] ${categoryText}`;
        } else if (reservationType === 'CONSULTING') {
          productText = `[${strings.consultingReservationTag}] ${categoryText}`;
        }
      }

      setReservationInfo({
        name,
        reservedAt: reservedAtText,
        partnerAddress: partnerAddress,
        partnerName: partnerNameText,
        expectPriceText,
        productText,
      });
    }
  }, [navigate, state]);

  const lottieJson = getReservationSuccessLottieData(colors.primary3);

  const renderCompleteText = () => {
    let title = strings.completeReservation;
    let desc =
      customLabel?.completeReservationDesc || strings.completeReservationDesc;
    let reservationProductOptions = [];
    if (state && state.reservationProductOptions) {
      reservationProductOptions = state.reservationProductOptions;
    }
    const isTreatmentReservation = reservationProductOptions.length > 0;

    if (partner) {
      const { homepageConfig } = partner;
      if (homepageConfig) {
        const {
          treatmentReservationAutoAccept,
          consultingReservationAutoAccept,
        } = homepageConfig;

        if (isTreatmentReservation && treatmentReservationAutoAccept) {
          title = strings.confirmReservation;
          desc =
            customLabel?.confirmReservationDesc ||
            strings.confirmReservationDesc;
        } else if (consultingReservationAutoAccept) {
          title = strings.confirmReservation;
          desc =
            customLabel?.confirmReservationDesc ||
            strings.confirmReservationDesc;
        }
      }
    }

    return (
      <>
        <div className="mt-2">
          <Typo type="b-24">{title}</Typo>
        </div>
        <div className="mt-2">
          <Typo
            style={{
              whiteSpace: 'pre-wrap',
            }}
            type="r-16"
            color={colors.subTitle}
          >
            {desc}
          </Typo>
        </div>
      </>
    );
  };

  return (
    <div
      className="h-full flex flex-col"
      style={{
        backgroundColor: colors.greyHover,
      }}
    >
      <div
        className="h-full flex-1"
        style={{
          padding: '0px 20px',
        }}
      >
        <div className="mt-[72px] text-center">
          <Lottie
            options={{
              autoplay: true,
              animationData: lottieJson,
              loop: false,
            }}
            width={68}
            height={68}
          />
          {renderCompleteText()}
        </div>

        <div
          className="flex flex-col"
          style={{
            marginTop: 24,
            backgroundColor: 'white',
            borderRadius: 20,
            padding: '16px 20px',
          }}
        >
          <Typo type="r-14" color={colors.subTitle}>
            {reservationInfo.partnerName}
          </Typo>
          <div className="mt-2">
            <Typo type="b-18">{reservationInfo.productText}</Typo>
          </div>

          <div className="mt-4 flex flex-col gap-3">
            <div className="flex justify-between items-center">
              <Typo
                type="r-14"
                color={colors.subTitle}
                style={{
                  whiteSpace: 'pre-wrap',
                }}
              >
                {strings.reservationDateTime}
              </Typo>
              <Typo type="m-14">{reservationInfo.reservedAt}</Typo>
            </div>
            {!isDrPetit && (
              <div className="flex justify-between">
                <Typo type="r-14" color={colors.subTitle}>
                  {strings.expectedPaymentPrice}
                </Typo>
                <Typo type="m-14">{reservationInfo.expectPriceText}</Typo>
              </div>
            )}
          </div>
        </div>
        {renderHardCodedReservationNotice(
          languageCode || 'ko',
          partner?.id || 0,
        )}
        <div className="flex flex-col gap-1 mt-4">
          <div className="flex items-start gap-1">
            <span
              style={{
                lineHeight: '10px',
                color: colors.subTitle2,
              }}
            >
              •
            </span>
            <Typo type="r-12" color={colors.subTitle2}>
              {customLabel?.reservationCancelPolicy1 ||
                strings.reservationCancelPolicy1}
            </Typo>
          </div>
          <div className="flex items-start gap-1">
            <span
              style={{
                lineHeight: '15px',
                color: colors.subTitle2,
              }}
            >
              •
            </span>
            <Typo
              type="r-12"
              style={{
                lineHeight: '20px',
              }}
              color={colors.subTitle2}
            >
              {customLabel?.reservationCancelPolicy2 ||
                strings.reservationCancelPolicy2}
            </Typo>
          </div>
        </div>
      </div>
      <div className="p-[20px]">
        <Button
          style={{
            width: '100%',
          }}
          onClick={() => {
            sendTutorialEndEvent();
            const isKakaoInApp = getUA.match(/KAKAOTALK/i);

            if (isKakaoInApp) {
              if (isAndroid) {
                window.location.href = 'kakaotalk://inappbrowser/close';
              }
              if (isIOS) {
                window.location.href = 'kakaoweb://closeBrowser';
              }
            } else {
              navigate({
                pathname: ROUTER_PATHS.RESERVATION,
                search,
              });
            }
          }}
        >
          {strings.confirm}
        </Button>
      </div>
    </div>
  );
}

export default ReservationCompletePage;
